import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Plus, Trash2, Package, Truck, Calculator, Settings2, RotateCcw, Info, Check, X, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';

const PACKAGE_COLORS = [
  '#C41E3A', '#2563eb', '#16a34a', '#ca8a04', '#9333ea', 
  '#dc2626', '#0891b2', '#65a30d', '#c026d3', '#ea580c'
];

// Get consistent color based on package ID (not index)
const getPackageColor = (pkgId) => {
  return PACKAGE_COLORS[pkgId % PACKAGE_COLORS.length];
};

const LdmCalculatorPage = () => {
  const { language } = useSiteContent();
  usePageTitle(language === 'bg' ? 'Калкулатор товарно пространство' : 'Cargo Space Calculator');

  const [trailerConfig, setTrailerConfig] = useState({ width: 240, length: 1360 });
  const [showConfig, setShowConfig] = useState(false);
  const [packages, setPackages] = useState([]);
  
  // New package form state
  const [showNewPackageForm, setShowNewPackageForm] = useState(false);
  const [newPackage, setNewPackage] = useState({ length: 120, width: 80, height: 100, quantity: 1 });
  const [errorMessage, setErrorMessage] = useState('');
  
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(0.4);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragPositionValid, setIsDragPositionValid] = useState(true);

  // Calculate scale - ensure minimum visual size
  useEffect(() => {
    const updateScale = () => {
      if (wrapperRef.current) {
        const containerWidth = wrapperRef.current.clientWidth - 32;
        // Minimum trailer visual width should be 500px or container width
        const minVisualWidth = Math.min(500, containerWidth);
        // Calculate scale to fit, but ensure minimum visual size
        const fitScale = containerWidth / trailerConfig.length;
        const minScale = minVisualWidth / trailerConfig.length;
        // Use the larger of: fit scale or minimum scale, capped at 1.0
        setScale(Math.min(Math.max(fitScale, minScale), 1.0));
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [trailerConfig.length]);

  const t = language === 'bg' ? {
    title: 'Калкулатор товарно пространство',
    subtitle: 'Изчислете обема и товарните метри на вашите пратки',
    trailerConfig: 'Настройки на ремаркето',
    trailerWidth: 'Ширина (cm)',
    trailerLength: 'Дължина (cm)',
    packages: 'Пакети',
    length: 'Дълж.',
    width: 'Шир.',
    height: 'Вис.',
    quantity: 'Бр.',
    addPackage: 'Добави пакет',
    confirmAdd: 'Потвърди',
    cancel: 'Отказ',
    reset: 'Нулиране',
    results: 'Резултати',
    totalVolume: 'Общ обем',
    totalLdm: 'Общо LDM',
    totalPackages: 'Брой пакети',
    trailerUsage: 'Запълване',
    occupiedSpace: 'Заето място',
    package: 'Пакет',
    trailerView: 'Товарно пространство',
    autoArranged: 'Автоматично подредени',
    doubleClickRotate: 'Двоен клик = завъртане',
    dragToMove: 'Плъзгане = преместване',
    tips: 'Съвети',
    emptyTrailer: 'Добавете пакети',
    newPackage: 'Нов пакет',
    noPackages: 'Няма добавени пакети',
    noSpace: 'Няма останало място в ремаркето!',
    packageTooBig: 'Пакетът е твърде голям за ремаркето!',
    partialFit: 'Само {count} от {total} пакета се побират'
  } : {
    title: 'Cargo Space Calculator',
    subtitle: 'Calculate volume and loading meters for your shipments',
    trailerConfig: 'Trailer Configuration',
    trailerWidth: 'Width (cm)',
    trailerLength: 'Length (cm)',
    packages: 'Packages',
    length: 'Len.',
    width: 'Wid.',
    height: 'Hei.',
    quantity: 'Qty',
    addPackage: 'Add Package',
    confirmAdd: 'Confirm',
    cancel: 'Cancel',
    reset: 'Reset',
    results: 'Results',
    totalVolume: 'Total Volume',
    totalLdm: 'Total LDM',
    totalPackages: 'Packages',
    trailerUsage: 'Usage',
    occupiedSpace: 'Occupied Space',
    package: 'Package',
    trailerView: 'Cargo Space',
    autoArranged: 'Auto-arranged',
    doubleClickRotate: 'Double-click = rotate',
    dragToMove: 'Drag = move',
    tips: 'Tips',
    emptyTrailer: 'Add packages',
    newPackage: 'New Package',
    noPackages: 'No packages added',
    noSpace: 'No space left in trailer!',
    packageTooBig: 'Package is too big for trailer!',
    partialFit: 'Only {count} of {total} packages fit in trailer'
  };

  // Find position for package
  const findPosition = useCallback((l, w, placements, rotated = false) => {
    const tLength = trailerConfig.length || 1360;
    const tWidth = trailerConfig.width || 240;
    
    const itemL = rotated ? w : l;
    const itemW = rotated ? l : w;
    
    // Check if package fits in trailer at all (strict check)
    if (itemL > tLength || itemW > tWidth) {
      // Try rotation only if not already rotated and dimensions are different
      if (!rotated && l !== w) {
        return findPosition(l, w, placements, true);
      }
      return null;
    }
    
    // Find valid position using 5cm grid
    for (let x = 0; x <= tLength - itemL; x += 5) {
      for (let y = 0; y <= tWidth - itemW; y += 5) {
        let ok = true;
        for (const p of placements) {
          const pL = p.rotated ? p.w : p.l;
          const pW = p.rotated ? p.l : p.w;
          // Check collision with tolerance
          if (x < p.x + pL && x + itemL > p.x && y < p.y + pW && y + itemW > p.y) {
            ok = false;
            break;
          }
        }
        if (ok) return { x, y, rotated };
      }
    }
    
    // Try rotation if not found and not already rotated
    if (!rotated && l !== w) {
      return findPosition(l, w, placements, true);
    }
    return null;
  }, [trailerConfig]);

  // Check how many packages can fit - simulates exact placement algorithm
  const checkFit = useCallback((newL, newW, newQ, existingPackages, currentManualPositions = {}, currentPlacedItems = []) => {
    const tLength = trailerConfig.length || 1360;
    const tWidth = trailerConfig.width || 240;
    
    // First check if package can fit at all (in either orientation)
    const fitsNormal = newL <= tLength && newW <= tWidth;
    const fitsRotated = newW <= tLength && newL <= tWidth;
    
    if (!fitsNormal && !fitsRotated) {
      return 0; // Package is too big in any orientation
    }
    
    const placements = [];
    
    // First, add all existing placed items with their actual positions
    currentPlacedItems.forEach(item => {
      placements.push({ 
        x: item.x, 
        y: item.y, 
        l: item.l, 
        w: item.w, 
        rotated: item.rotated 
      });
    });
    
    // Now try to fit new packages
    let fittedNewCount = 0;
    
    for (let i = 0; i < newQ; i++) {
      const pos = findPosition(newL, newW, placements);
      if (pos) {
        placements.push({ x: pos.x, y: pos.y, l: newL, w: newW, rotated: pos.rotated });
        fittedNewCount++;
      }
    }
    
    return fittedNewCount;
  }, [findPosition, trailerConfig]);

  const [manualPositions, setManualPositions] = useState({});

  const getItemPosition = useCallback((item) => {
    const manual = manualPositions[item.id];
    if (manual) return manual;
    return { x: item.x, y: item.y, rotated: item.rotated };
  }, [manualPositions]);

  // Generate placed items
  const placedItems = useMemo(() => {
    const items = [];
    const placements = [];
    
    const tLength = trailerConfig.length || 1360;
    const tWidth = trailerConfig.width || 240;
    
    const allPkgs = [];
    packages.forEach((pkg, idx) => {
      const l = Number(pkg.length) || 0;
      const w = Number(pkg.width) || 0;
      const q = Number(pkg.quantity) || 0;
      if (l > 0 && w > 0 && q > 0) {
        for (let i = 0; i < q; i++) {
          const itemId = `${pkg.id}-${i}`;
          allPkgs.push({ 
            pkgId: pkg.id, 
            idx: i, 
            itemId,
            l, 
            w, 
            area: l * w, 
            color: getPackageColor(pkg.id),
            hasManualPosition: !!manualPositions[itemId]
          });
        }
      }
    });
    
    // Sort: items with manual positions first (to reserve their space), then by area
    allPkgs.sort((a, b) => {
      if (a.hasManualPosition && !b.hasManualPosition) return -1;
      if (!a.hasManualPosition && b.hasManualPosition) return 1;
      return b.area - a.area;
    });
    
    allPkgs.forEach(item => {
      // Check if this item has a manual position
      const manualPos = manualPositions[item.itemId];
      
      if (manualPos) {
        // Use manual position
        const itemL = manualPos.rotated ? item.w : item.l;
        const itemW = manualPos.rotated ? item.l : item.w;
        placements.push({ x: manualPos.x, y: manualPos.y, l: item.l, w: item.w, rotated: manualPos.rotated });
        items.push({
          id: item.itemId,
          pkgId: item.pkgId,
          idx: item.idx,
          l: item.l,
          w: item.w,
          x: manualPos.x,
          y: manualPos.y,
          rotated: manualPos.rotated,
          color: item.color
        });
      } else {
        // Find automatic position
        const pos = findPosition(item.l, item.w, placements);
        if (pos) {
          const itemL = pos.rotated ? item.w : item.l;
          const itemW = pos.rotated ? item.l : item.w;
          const safeX = Math.min(pos.x, tLength - itemL);
          const safeY = Math.min(pos.y, tWidth - itemW);
          
          placements.push({ x: safeX, y: safeY, l: item.l, w: item.w, rotated: pos.rotated });
          items.push({
            id: item.itemId,
            pkgId: item.pkgId,
            idx: item.idx,
            l: item.l,
            w: item.w,
            x: safeX,
            y: safeY,
            rotated: pos.rotated,
            color: item.color
          });
        }
      }
    });
    
    return items;
  }, [packages, findPosition, trailerConfig, manualPositions]);

  // Calculate actual occupied space based on furthest pallet positions
  const occupiedSpace = useMemo(() => {
    if (placedItems.length === 0) {
      return { length: 0, width: 0 };
    }
    
    let maxX = 0;
    let maxY = 0;
    
    placedItems.forEach(item => {
      const itemL = item.rotated ? item.w : item.l;
      const itemW = item.rotated ? item.l : item.w;
      const rightEdge = item.x + itemL;
      const bottomEdge = item.y + itemW;
      
      if (rightEdge > maxX) maxX = rightEdge;
      if (bottomEdge > maxY) maxY = bottomEdge;
    });
    
    return {
      length: (maxX / 100).toFixed(2),
      width: (maxY / 100).toFixed(2)
    };
  }, [placedItems]);

  // Calculations
  const calculations = useMemo(() => {
    let totalVolume = 0;
    let totalLdm = 0;
    let totalPkgs = 0;

    packages.forEach(pkg => {
      const l = Number(pkg.length) || 0;
      const w = Number(pkg.width) || 0;
      const h = Number(pkg.height) || 0;
      const q = Number(pkg.quantity) || 0;
      
      totalVolume += (l * w * h * q) / 1000000;
      totalLdm += (l * w * q) / (trailerConfig.width * 100);
      totalPkgs += q;
    });

    const maxLdm = trailerConfig.length / 100;
    return {
      totalVolume: totalVolume.toFixed(3),
      totalLdm: totalLdm.toFixed(2),
      totalPkgs,
      usage: maxLdm > 0 ? Math.min((totalLdm / maxLdm) * 100, 100).toFixed(1) : '0.0',
      maxLdm: maxLdm.toFixed(2)
    };
  }, [packages, trailerConfig]);

  // Add confirmed package
  const confirmAddPackage = () => {
    const l = Number(newPackage.length) || 0;
    const w = Number(newPackage.width) || 0;
    const h = Number(newPackage.height) || 0;
    const q = Number(newPackage.quantity) || 0;
    
    setErrorMessage('');
    
    if (l <= 0 || w <= 0 || q <= 0) return;
    
    const tLength = trailerConfig.length || 1360;
    const tWidth = trailerConfig.width || 240;
    
    // Check if single package can fit in trailer (in either orientation)
    const fitsNormal = l <= tLength && w <= tWidth;
    const fitsRotated = w <= tLength && l <= tWidth;
    
    if (!fitsNormal && !fitsRotated) {
      setErrorMessage(t.packageTooBig);
      return;
    }
    
    // Check how many can fit using actual placed positions
    const fittedCount = checkFit(l, w, q, packages, manualPositions, placedItems);
    
    if (fittedCount === 0) {
      setErrorMessage(t.noSpace);
      return;
    }
    
    // IMPORTANT: Save current positions of all existing items before adding new package
    // This prevents the re-sorting algorithm from moving existing packages
    const currentPositions = {};
    placedItems.forEach(item => {
      const pos = getItemPosition(item);
      currentPositions[item.id] = { x: pos.x, y: pos.y, rotated: pos.rotated };
    });
    setManualPositions(prev => ({ ...prev, ...currentPositions }));
    
    if (fittedCount < q) {
      setErrorMessage(t.partialFit.replace('{count}', fittedCount).replace('{total}', q));
      // Add only the ones that fit
      const newId = packages.length > 0 ? Math.max(...packages.map(p => p.id)) + 1 : 1;
      setPackages([...packages, { id: newId, length: l, width: w, height: h, quantity: fittedCount }]);
    } else {
      // All fit
      const newId = packages.length > 0 ? Math.max(...packages.map(p => p.id)) + 1 : 1;
      setPackages([...packages, { id: newId, length: l, width: w, height: h, quantity: q }]);
    }
    
    setShowNewPackageForm(false);
    setNewPackage({ length: 120, width: 80, height: 100, quantity: 1 });
  };

  const cancelAddPackage = () => {
    setShowNewPackageForm(false);
    setNewPackage({ length: 120, width: 80, height: 100, quantity: 1 });
    setErrorMessage('');
  };

  const removePackage = (id) => {
    // Save current positions of ALL remaining items before deletion
    // This prevents them from being re-placed automatically
    const newManualPositions = {};
    placedItems.forEach(item => {
      // Keep items that don't belong to the deleted package
      if (item.pkgId !== id) {
        const pos = getItemPosition(item);
        newManualPositions[item.id] = { x: pos.x, y: pos.y, rotated: pos.rotated };
      }
    });
    setManualPositions(newManualPositions);
    setPackages(packages.filter(p => p.id !== id));
  };

  const resetPackages = () => {
    setPackages([]);
    setManualPositions({});
    setShowNewPackageForm(false);
  };

  // Drag handlers
  // Calculate effective scale based on actual container size
  const getEffectiveScale = useCallback(() => {
    if (!containerRef.current) return { scaleX: scale, scaleY: scale };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      scaleX: rect.width / trailerConfig.length,
      scaleY: rect.height / trailerConfig.width
    };
  }, [scale, trailerConfig]);

  const handleDragStart = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pos = getItemPosition(item);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const scaleX = rect.width / trailerConfig.length;
    const scaleY = rect.height / trailerConfig.width;
    
    // Store original position as last valid
    lastValidPositions.current[item.id] = { x: pos.x, y: pos.y };
    
    setDragging({ ...item, ...pos });
    setDragOffset({ x: clientX - (rect.left + pos.x * scaleX), y: clientY - (rect.top + pos.y * scaleY) });
  };

  // Check if position overlaps with any other package
  const hasOverlap = useCallback((x, y, itemL, itemW, excludeId) => {
    const TOLERANCE = 1; // 1cm tolerance
    for (const item of placedItems) {
      if (item.id === excludeId) continue;
      const pos = getItemPosition(item);
      const pL = pos.rotated ? item.w : item.l;
      const pW = pos.rotated ? item.l : item.w;
      
      // Check if rectangles overlap
      const noOverlap = x + itemL <= pos.x + TOLERANCE || 
                        pos.x + pL <= x + TOLERANCE || 
                        y + itemW <= pos.y + TOLERANCE || 
                        pos.y + pW <= y + TOLERANCE;
      
      if (!noOverlap) {
        return true;
      }
    }
    return false;
  }, [placedItems, getItemPosition]);

  // Magnetic snap distance
  const MAGNETIC_DISTANCE = 10; // Snap when within 10cm of edge

  // Find the best magnetic snap position
  const findMagneticPosition = useCallback((x, y, itemL, itemW, excludeId) => {
    let bestSnapX = x;
    let bestSnapY = y;
    let snappedX = false;
    let snappedY = false;
    
    // Collect all snap targets (walls + package edges)
    const xTargets = [0, trailerConfig.length - itemL]; // walls
    const yTargets = [0, trailerConfig.width - itemW]; // walls
    
    // Add package edges as targets
    for (const item of placedItems) {
      if (item.id === excludeId) continue;
      const pos = getItemPosition(item);
      const pL = pos.rotated ? item.w : item.l;
      const pW = pos.rotated ? item.l : item.w;
      
      // X targets: left edge of package, right edge of package
      xTargets.push(pos.x - itemL); // snap our right edge to their left edge
      xTargets.push(pos.x + pL);     // snap our left edge to their right edge
      
      // Y targets
      yTargets.push(pos.y - itemW); // snap our bottom to their top
      yTargets.push(pos.y + pW);     // snap our top to their bottom
    }
    
    // Find closest X target within magnetic distance
    let minDistX = MAGNETIC_DISTANCE;
    for (const targetX of xTargets) {
      if (targetX < 0 || targetX > trailerConfig.length - itemL) continue;
      const dist = Math.abs(x - targetX);
      if (dist < minDistX) {
        minDistX = dist;
        bestSnapX = targetX;
        snappedX = true;
      }
    }
    
    // Find closest Y target within magnetic distance
    let minDistY = MAGNETIC_DISTANCE;
    for (const targetY of yTargets) {
      if (targetY < 0 || targetY > trailerConfig.width - itemW) continue;
      const dist = Math.abs(y - targetY);
      if (dist < minDistY) {
        minDistY = dist;
        bestSnapY = targetY;
        snappedY = true;
      }
    }
    
    // Validate the snapped position doesn't cause overlap
    const finalX = snappedX ? bestSnapX : x;
    const finalY = snappedY ? bestSnapY : y;
    
    if (!hasOverlap(finalX, finalY, itemL, itemW, excludeId)) {
      return { x: finalX, y: finalY, valid: true };
    }
    
    // Try with only X snap
    if (snappedX && !hasOverlap(bestSnapX, y, itemL, itemW, excludeId)) {
      return { x: bestSnapX, y: y, valid: true };
    }
    
    // Try with only Y snap
    if (snappedY && !hasOverlap(x, bestSnapY, itemL, itemW, excludeId)) {
      return { x: x, y: bestSnapY, valid: true };
    }
    
    // Try original position
    if (!hasOverlap(x, y, itemL, itemW, excludeId)) {
      return { x, y, valid: true };
    }
    
    return { x, y, valid: false };
  }, [placedItems, getItemPosition, trailerConfig, hasOverlap]);

  // Find valid position - uses magnetic snapping first, then collision resolution
  const findSnapPosition = useCallback((x, y, itemL, itemW, excludeId) => {
    // Try magnetic snapping first
    const magneticResult = findMagneticPosition(x, y, itemL, itemW, excludeId);
    if (magneticResult.valid) {
      return magneticResult;
    }
    
    // If position is invalid, find nearest valid edge position
    const snapCandidates = [];
    
    for (const item of placedItems) {
      if (item.id === excludeId) continue;
      const pos = getItemPosition(item);
      const pL = pos.rotated ? item.w : item.l;
      const pW = pos.rotated ? item.l : item.w;
      
      // Generate candidates around this package
      const candidates = [
        { x: pos.x - itemL, y: y },      // left of package
        { x: pos.x + pL, y: y },          // right of package
        { x: x, y: pos.y - itemW },       // above package
        { x: x, y: pos.y + pW },          // below package
      ];
      
      for (const c of candidates) {
        // Clamp to trailer bounds
        c.x = Math.max(0, Math.min(c.x, trailerConfig.length - itemL));
        c.y = Math.max(0, Math.min(c.y, trailerConfig.width - itemW));
        c.dist = Math.sqrt(Math.pow(x - c.x, 2) + Math.pow(y - c.y, 2));
        snapCandidates.push(c);
      }
    }
    
    // Sort by distance
    snapCandidates.sort((a, b) => a.dist - b.dist);
    
    // Find first valid candidate
    for (const c of snapCandidates) {
      if (!hasOverlap(c.x, c.y, itemL, itemW, excludeId)) {
        return { x: c.x, y: c.y, valid: true };
      }
    }
    
    return { x, y, valid: false };
  }, [placedItems, getItemPosition, trailerConfig, hasOverlap, findMagneticPosition]);

  // Store last valid position for each item
  const lastValidPositions = useRef({});
  // Store temporary (possibly invalid) position during drag
  const tempDragPosition = useRef(null);

  const handleDragMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Use actual container size divided by trailer dimensions for effective scale
    const scaleX = rect.width / trailerConfig.length;
    const scaleY = rect.height / trailerConfig.width;
    
    const itemL = dragging.rotated ? dragging.w : dragging.l;
    const itemW = dragging.rotated ? dragging.l : dragging.w;
    
    // Calculate position in trailer coordinates, clamped to boundaries only
    const newX = Math.max(0, Math.min((clientX - rect.left - dragOffset.x) / scaleX, trailerConfig.length - itemL));
    const newY = Math.max(0, Math.min((clientY - rect.top - dragOffset.y) / scaleY, trailerConfig.width - itemW));
    
    // Store temp position
    tempDragPosition.current = { x: newX, y: newY };
    
    // Check if position is valid and update visual indicator
    const isValid = !hasOverlap(newX, newY, itemL, itemW, dragging.id);
    setIsDragPositionValid(isValid);
    
    if (isValid) {
      // Save as last valid position
      lastValidPositions.current[dragging.id] = { x: newX, y: newY };
    }
    
    // Always update visual position (allow passing through other packages)
    setManualPositions(prev => ({ ...prev, [dragging.id]: { x: newX, y: newY, rotated: dragging.rotated } }));
  }, [dragging, dragOffset, trailerConfig, hasOverlap]);

  const handleDragEnd = useCallback(() => {
    if (dragging && tempDragPosition.current) {
      const itemL = dragging.rotated ? dragging.w : dragging.l;
      const itemW = dragging.rotated ? dragging.l : dragging.w;
      const { x, y } = tempDragPosition.current;
      
      // Check if final position is valid
      const isValid = !hasOverlap(x, y, itemL, itemW, dragging.id);
      
      if (!isValid) {
        // Position is invalid - try to find nearby valid position
        const snapResult = findSnapPosition(x, y, itemL, itemW, dragging.id);
        
        if (snapResult.valid) {
          // Snap to valid position
          setManualPositions(prev => ({ ...prev, [dragging.id]: { x: snapResult.x, y: snapResult.y, rotated: dragging.rotated } }));
        } else if (lastValidPositions.current[dragging.id]) {
          // Revert to last valid position
          const last = lastValidPositions.current[dragging.id];
          setManualPositions(prev => ({ ...prev, [dragging.id]: { x: last.x, y: last.y, rotated: dragging.rotated } }));
        }
      }
    }
    tempDragPosition.current = null;
    setIsDragPositionValid(true);
    setDragging(null);
  }, [dragging, hasOverlap, findSnapPosition]);

  const handleDoubleClick = (item) => {
    const pos = getItemPosition(item);
    const newRotated = !pos.rotated;
    const newL = newRotated ? item.w : item.l;
    const newW = newRotated ? item.l : item.w;
    
    // Check if rotated item fits in trailer
    if (newL > trailerConfig.length || newW > trailerConfig.width) {
      // Rotation not possible - item would exceed trailer dimensions
      return;
    }
    
    // Calculate new position ensuring item stays within bounds
    const newX = Math.min(pos.x, trailerConfig.length - newL);
    const newY = Math.min(pos.y, trailerConfig.width - newW);
    
    // Check if rotation would cause overlap
    if (hasOverlap(newX, newY, newL, newW, item.id)) {
      // Try to find a valid position nearby
      const validPos = findSnapPosition(newX, newY, newL, newW, item.id);
      if (validPos.valid) {
        setManualPositions(prev => ({
          ...prev,
          [item.id]: {
            x: validPos.x,
            y: validPos.y,
            rotated: newRotated
          }
        }));
      }
      // If no valid position found, rotation is not possible
      return;
    }
    
    setManualPositions(prev => ({
      ...prev,
      [item.id]: {
        x: newX,
        y: newY,
        rotated: newRotated
      }
    }));
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [dragging, handleDragMove, handleDragEnd]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-24 pb-16">
      <SEO page="ldm-calculator" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C41E3A]/10 rounded-2xl mb-4">
            <Calculator className="w-8 h-8 text-[#C41E3A]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">{t.title}</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-4">
            
            {/* Trailer Config */}
            <Card className="bg-white rounded-2xl shadow-lg border-0 overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => setShowConfig(!showConfig)}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#C41E3A]/10 rounded-lg flex items-center justify-center">
                    <Truck className="w-4 h-4 text-[#C41E3A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">{t.trailerConfig}</h3>
                    <p className="text-xs text-slate-500">{(trailerConfig.width || 240) / 100}m × {(trailerConfig.length || 1360) / 100}m</p>
                  </div>
                </div>
                <Settings2 className={`w-4 h-4 text-slate-400 transition-transform ${showConfig ? 'rotate-90' : ''}`} />
              </div>
              {showConfig && (
                <CardContent className="p-3 border-t border-slate-100">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t.trailerWidth}</Label>
                      <Input 
                        type="number"
                        inputMode="numeric"
                        value={trailerConfig.width} 
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : Math.min(Number(e.target.value), 300);
                          setTrailerConfig(prev => ({...prev, width: val}));
                        }}
                        onBlur={(e) => {
                          const val = Number(e.target.value);
                          if (!val || val <= 0) {
                            setTrailerConfig(prev => ({...prev, width: 240}));
                          } else if (val > 300) {
                            setTrailerConfig(prev => ({...prev, width: 300}));
                          }
                        }}
                        className="mt-1 h-8 text-base" 
                        min="50"
                        max="300"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t.trailerLength}</Label>
                      <Input 
                        type="number"
                        inputMode="numeric"
                        value={trailerConfig.length} 
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : Math.min(Number(e.target.value), 2000);
                          setTrailerConfig(prev => ({...prev, length: val}));
                        }}
                        onBlur={(e) => {
                          const val = Number(e.target.value);
                          if (!val || val <= 0) {
                            setTrailerConfig(prev => ({...prev, length: 1360}));
                          } else if (val > 2000) {
                            setTrailerConfig(prev => ({...prev, length: 2000}));
                          }
                        }}
                        className="mt-1 h-8 text-base" 
                        min="100"
                        max="2000"
                      />
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Packages */}
            <Card className="bg-white rounded-2xl shadow-lg border-0">
              <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#C41E3A]/10 rounded-lg flex items-center justify-center">
                    <Package className="w-4 h-4 text-[#C41E3A]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm">{t.packages}</h3>
                </div>
                {packages.length > 0 && (
                  <Button onClick={resetPackages} variant="ghost" size="sm" className="text-slate-500 h-7 text-xs">
                    <RotateCcw className="w-3 h-3 mr-1" />{t.reset}
                  </Button>
                )}
              </div>
              
              <CardContent className="p-3 space-y-3 max-h-[300px] overflow-y-auto">
                {packages.length === 0 && !showNewPackageForm && (
                  <div className="text-center py-4 text-slate-400 text-sm">
                    {t.noPackages}
                  </div>
                )}
                
                {packages.map((pkg, index) => (
                  <div key={pkg.id} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: getPackageColor(pkg.id) }} />
                        <span className="font-medium text-slate-700 text-sm">
                          {pkg.length}×{pkg.width}×{pkg.height}cm
                        </span>
                        <span className="text-slate-400 text-xs">×{pkg.quantity}</span>
                      </div>
                      <Button onClick={() => removePackage(pkg.id)} variant="ghost" size="sm" className="text-red-500 h-6 w-6 p-0">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
              
              {/* Add Package Section */}
              <div className="p-3 border-t border-slate-100">
                {!showNewPackageForm ? (
                  <Button onClick={() => setShowNewPackageForm(true)} variant="outline" className="w-full rounded-xl border-dashed border-2 border-slate-300 hover:border-[#C41E3A] hover:text-[#C41E3A] h-10">
                    <Plus className="w-4 h-4 mr-2" />{t.addPackage}
                  </Button>
                ) : (
                  <div className="bg-blue-50 rounded-xl p-3 border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-900 text-sm">{t.newPackage}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      <div>
                        <Label className="text-[10px] text-slate-500">{t.length}</Label>
                        <Input 
                          type="number" 
                          inputMode="numeric"
                          value={newPackage.length} 
                          onChange={(e) => setNewPackage({...newPackage, length: e.target.value})} 
                          className="mt-1 h-8 text-base px-2" 
                          style={{ fontSize: '16px' }}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">{t.width}</Label>
                        <Input 
                          type="number" 
                          inputMode="numeric"
                          value={newPackage.width} 
                          onChange={(e) => setNewPackage({...newPackage, width: e.target.value})} 
                          className="mt-1 h-8 text-base px-2" 
                          style={{ fontSize: '16px' }}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">{t.height}</Label>
                        <Input 
                          type="number" 
                          inputMode="numeric"
                          value={newPackage.height} 
                          onChange={(e) => setNewPackage({...newPackage, height: e.target.value})} 
                          className="mt-1 h-8 text-base px-2" 
                          style={{ fontSize: '16px' }}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">{t.quantity}</Label>
                        <Input 
                          type="number" 
                          inputMode="numeric"
                          value={newPackage.quantity} 
                          onChange={(e) => setNewPackage({...newPackage, quantity: e.target.value})} 
                          className="mt-1 h-8 text-base px-2" 
                          style={{ fontSize: '16px' }}
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={confirmAddPackage} size="sm" className="flex-1 bg-[#C41E3A] hover:bg-[#a01830] text-white rounded-lg h-8">
                        <Check className="w-3 h-3 mr-1" />{t.confirmAdd}
                      </Button>
                      <Button onClick={cancelAddPackage} variant="outline" size="sm" className="rounded-lg h-8">
                        <X className="w-3 h-3 mr-1" />{t.cancel}
                      </Button>
                    </div>
                    {errorMessage && (
                      <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700 text-xs">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>{errorMessage}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Results */}
            <Card className="bg-gradient-to-br from-[#C41E3A] to-[#8B1428] rounded-2xl shadow-xl border-0 text-white">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />{t.results}
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white/10 rounded-xl p-2 text-center">
                    <div className="text-white/70 text-[10px] mb-1">{t.totalVolume}</div>
                    <div className="text-lg font-bold font-mono">{calculations.totalVolume}</div>
                    <div className="text-[10px] text-white/50">m³</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-2 text-center">
                    <div className="text-white/70 text-[10px] mb-1">{t.totalLdm}</div>
                    <div className="text-lg font-bold font-mono">{calculations.totalLdm}</div>
                    <div className="text-[10px] text-white/50">LDM</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-2 text-center">
                    <div className="text-white/70 text-[10px] mb-1">{t.totalPackages}</div>
                    <div className="text-lg font-bold font-mono">{calculations.totalPkgs}</div>
                    <div className="text-[10px] text-white/50">pcs</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-2 text-center">
                    <div className="text-white/70 text-[10px] mb-1">{t.occupiedSpace}</div>
                    <div className="text-lg font-bold font-mono">{occupiedSpace.length} × {occupiedSpace.width}</div>
                    <div className="text-[10px] text-white/50">m</div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-white/70">{t.trailerUsage}</span>
                    <span className="font-semibold">{calculations.usage}%</span>
                  </div>
                  <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${calculations.usage}%` }} />
                  </div>
                  <div className="text-[10px] text-white/50 mt-1 text-center">{calculations.totalLdm} / {calculations.maxLdm} LDM</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Trailer */}
          <div className="lg:col-span-2" ref={wrapperRef}>
            <Card className="bg-white rounded-2xl shadow-lg border-0">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C41E3A]/10 rounded-xl flex items-center justify-center">
                      <Truck className="w-5 h-5 text-[#C41E3A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{t.trailerView}</h3>
                      <p className="text-xs text-slate-500">{t.autoArranged}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-500">
                    <span className="bg-slate-100 px-2 py-1 rounded-full">{t.doubleClickRotate}</span>
                    <span className="bg-slate-100 px-2 py-1 rounded-full">{t.dragToMove}</span>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-4">
                <div className="overflow-x-auto pb-4">
                  {/* TRAILER - Simple Rectangle */}
                  <div
                    ref={containerRef}
                    className="relative bg-slate-100 border-2 border-slate-400 mx-auto overflow-hidden"
                    style={{
                      width: trailerConfig.length * scale,
                      height: trailerConfig.width * scale,
                      minWidth: 280,
                      minHeight: 80,
                    }}
                  >
                    {/* Grid lines */}
                    {Array.from({ length: Math.floor(trailerConfig.length / 100) }).map((_, i) => (
                      <div key={i} className="absolute top-0 bottom-0 border-l border-dashed border-slate-300" style={{ left: `${((i + 1) * 100 / trailerConfig.length) * 100}%` }}>
                        <span className="absolute -top-4 -translate-x-1/2 text-[8px] text-slate-400">{i + 1}m</span>
                      </div>
                    ))}

                    {/* Packages */}
                    {placedItems.map(item => {
                      const pos = getItemPosition(item);
                      const dispL = pos.rotated ? item.w : item.l;
                      const dispW = pos.rotated ? item.l : item.w;
                      // Use percentage-based positioning for perfect boundary alignment
                      const leftPct = (pos.x / trailerConfig.length) * 100;
                      const topPct = (pos.y / trailerConfig.width) * 100;
                      const widthPct = (dispL / trailerConfig.length) * 100;
                      const heightPct = (dispW / trailerConfig.width) * 100;
                      
                      const isBeingDragged = dragging?.id === item.id;
                      const showInvalidIndicator = isBeingDragged && !isDragPositionValid;
                      const showValidIndicator = isBeingDragged && isDragPositionValid;
                      
                      return (
                        <div
                          key={item.id}
                          className={`absolute cursor-grab active:cursor-grabbing select-none flex items-center justify-center text-white text-[8px] font-bold transition-shadow ${
                            isBeingDragged 
                              ? 'z-50' 
                              : 'hover:z-10 border border-white/50'
                          }`}
                          style={{ 
                            left: `${leftPct}%`, 
                            top: `${topPct}%`, 
                            width: `${widthPct}%`, 
                            height: `${heightPct}%`, 
                            backgroundColor: item.color,
                            opacity: isBeingDragged ? 0.85 : 1,
                            boxShadow: showInvalidIndicator 
                              ? '0 0 0 3px #ef4444, 0 0 12px rgba(239,68,68,0.5)' 
                              : showValidIndicator 
                                ? '0 0 0 3px #22c55e, 0 0 12px rgba(34,197,94,0.5)' 
                                : 'none',
                            border: isBeingDragged ? 'none' : undefined
                          }}
                          onMouseDown={(e) => handleDragStart(e, item)}
                          onTouchStart={(e) => handleDragStart(e, item)}
                          onDoubleClick={() => handleDoubleClick(item)}
                        >
                          {(widthPct * heightPct > 100) && <span>{dispL}×{dispW}</span>}
                        </div>
                      );
                    })}

                    {/* Empty state */}
                    {placedItems.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                        {t.emptyTrailer}
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-3 text-xs text-slate-500">
                    {trailerConfig.length / 100}m × {trailerConfig.width / 100}m
                  </div>
                </div>

                {/* Legend */}
                {placedItems.length > 0 && (
                  <div className="mt-2 pt-3 border-t border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {packages.map((pkg, idx) => {
                        const l = Number(pkg.length) || 0;
                        const w = Number(pkg.width) || 0;
                        const h = Number(pkg.height) || 0;
                        const q = Number(pkg.quantity) || 0;
                        if (l <= 0 || w <= 0) return null;
                        return (
                          <div key={pkg.id} className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-slate-50 px-2 py-1 rounded-full">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: getPackageColor(pkg.id) }} />
                            <span>{l}×{w}{h > 0 ? `×${h}` : ''}cm × {q}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tips */}
                <div className="mt-4 p-3 bg-blue-50 rounded-xl text-[11px] text-blue-800 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>{t.tips}:</strong> {t.doubleClickRotate} • {t.dragToMove}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LdmCalculatorPage;
